import type { FC } from 'react'

import { Image } from '@skytech/ui/Image'
import { Td } from '@skytech/ui/Table/Td'
import dayjs from 'dayjs'
import ImageOffOutlineIcon from 'mdi-react/ImageOffOutlineIcon'

import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { TrLink } from '@/components/TrLink'
import { APP_ROUTES } from '@/util/app-routes'
import { imagePlaceholder } from '@/util/misc'

import { getStatusName } from '../helpers'
import type { PolicyOrder } from '../types'

import './EcomPolicyOrderListItem.styles.scss'

const EcomPolicyOrderListItem: FC<PolicyOrder & { searchFilter: string }> = ({
  id,
  name,
  policyName,
  customerName,
  departmentName,
  orderDate,
  statusId,
  coverAmount,
  remainderAmount,
  cost,
  currency,
  productImages,
  productNames,
  searchFilter,
}) => {
  const translate = useTranslation()
  const statusName = getStatusName(statusId)

  const imageList = productImages?.split('|') || []

  return (
    <TrLink
      path={APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(':ecomPolicyOrderId', String(id))}
      className="ecom-policy-order-list-item"
    >
      <Td className="user">
        <div className="user__name">
          <Highlight pattern={searchFilter} text={name} />
        </div>

        {policyName && (
          <div className="user__policy">
            <Highlight pattern={searchFilter} text={policyName} />
          </div>
        )}
      </Td>

      <Td className="mobile-view">
        <div className="mobile-view__products">
          <Images imgUrlList={imageList} />
        </div>

        <div>
          <div className="user__name">
            <Highlight pattern={searchFilter} text={name || customerName} />
          </div>

          <div className="order-date">{dayjs(orderDate).format('DD.MM.YYYY')}</div>

          <div className="cost">
            {coverAmount || remainderAmount ? (
              <div>
                <FormattedNumberWithCurrency amount={coverAmount} currency={currency} /> /{' '}
                <FormattedNumberWithCurrency
                  amount={coverAmount + remainderAmount}
                  currency={currency}
                />
              </div>
            ) : (
              <FormattedNumberWithCurrency amount={cost || 0} currency={currency} />
            )}
          </div>
        </div>
      </Td>

      <Td className="customer">
        <div className="customer__department">
          <Highlight pattern={searchFilter} text={departmentName} />
        </div>

        <div className="customer__name">
          <Highlight pattern={searchFilter} text={customerName} />
        </div>
      </Td>

      <Td className="products" title={productNames?.replaceAll('|', ', ')}>
        <Images imgUrlList={imageList} />
      </Td>

      <Td>{dayjs(orderDate).format('DD.MM.YYYY')}</Td>

      <Td>
        <Highlight pattern={searchFilter} text={translate(`ecom_order_status/${statusName}`)} />
      </Td>

      <Td className="cost number">
        {coverAmount || remainderAmount ? (
          <>
            <div className="numerator">
              <FormattedNumberWithCurrency amount={coverAmount} currency={currency} />
            </div>
            <div className="denominator">
              /{' '}
              <FormattedNumberWithCurrency
                amount={coverAmount + remainderAmount}
                currency={currency}
              />
            </div>
          </>
        ) : (
          <FormattedNumberWithCurrency amount={cost || 0} currency={currency} />
        )}
      </Td>
    </TrLink>
  )
}

const Images: FC<{ imgUrlList: string[] }> = ({ imgUrlList }) => (
  <>
    {imgUrlList.map((image, idx) => (
      <div className="products__image" key={idx}>
        {image ? (
          <Image src={image} alt="product" onError={imagePlaceholder} />
        ) : (
          <ImageOffOutlineIcon className="missing-image-placeholder" />
        )}
      </div>
    ))}
  </>
)

export default EcomPolicyOrderListItem
